/**
 * React hook that fetches dashboard data once and caches it for the session.
 * Returns loading / error / data and the authenticated user from the Lambda.
 *
 * If the Lambda responds 401 the auth context's signOut() is called so the
 * SignInScreen takes over.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { DashboardObservation } from './types';
import { fetchDashboardData, type DashboardUser } from './api';
import { useAuth } from '@/lib/auth/AuthContext';

interface UseDashboardDataResult {
  observations: DashboardObservation[];
  loading: boolean;
  error: string | null;
  fetchedAt: string | null;
  user: DashboardUser | null;
  refetch: () => void;
}

export function useDashboardData(): UseDashboardDataResult {
  const { session, signOut } = useAuth();
  const [observations, setObservations] = useState<DashboardObservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [user, setUser] = useState<DashboardUser | null>(null);
  const didFetch = useRef(false);

  const load = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchDashboardData(session.idToken);
      if (result.authError) {
        signOut();
        return;
      }
      if (!result.ok) {
        setError(result.error ?? 'Unknown error');
        setObservations([]);
      } else {
        setObservations(result.observations);
        setUser(result.user ?? null);
      }
      setFetchedAt(result.fetchedAt);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fetch failed');
      setObservations([]);
    } finally {
      setLoading(false);
    }
  }, [session, signOut]);

  useEffect(() => {
    if (didFetch.current || !session) return;
    didFetch.current = true;
    void load();
  }, [load, session]);

  return { observations, loading, error, fetchedAt, user, refetch: load };
}
